// Sopwith 3D - title tune notation.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Mission files carry the title music as a compact note string, one note
// per `/`-terminated token: a letter a-g (r for a rest), optional `+`/`-`
// for sharp/flat, a duration denominator (4 = crotchet), optional `.` for a
// dotted note, and `<`/`>` to shift the octave down/up for the rest of the
// tune. Ported from SDL Sopwith swsound.c; timing is in 10 Hz ticks.

export interface TuneNote {
  /** Frequency in Hz, or 0 for a rest. */
  freq: number;
  /** Duration in simulation ticks (tenths of a second). */
  ticks: number;
}

/** Equal-tempered octave starting at C5, as the PC speaker driver used. */
const NOTE_FREQ = [523, 554, 587, 622, 659, 698, 740, 784, 831, 880, 932, 988];
/** Semitone index of a..g within NOTE_FREQ. */
const NOTE_INDEX: Record<string, number> = { a: 9, b: 11, c: 0, d: 2, e: 4, f: 5, g: 7 };

export function parseTune(text: string): TuneNote[] {
  const notes: TuneNote[] = [];
  let octave = 256; // x256 fixed point, carried across notes
  for (const raw of text.split('/')) {
    const token = raw.trim().toLowerCase();
    if (!token) continue;
    let letter = '';
    let semitoneAdj = 0;
    let dotted = 2;
    let durText = '';
    for (const ch of token) {
      if (ch >= 'a' && ch <= 'z') {
        letter = ch;
      } else if (ch === '>') {
        octave <<= 1;
      } else if (ch === '<') {
        octave >>= 1;
      } else if (ch === '+') {
        semitoneAdj++;
      } else if (ch === '-') {
        semitoneAdj--;
      } else if (ch === '.') {
        dotted = 3;
      } else if (ch >= '0' && ch <= '9') {
        durText += ch;
      }
    }
    let denom = parseInt(durText, 10);
    if (!(denom > 0)) denom = 4;
    const ticks = Math.floor(Math.floor((1440 * dotted) / (60 * denom)) / 2);
    if (!letter || letter === 'r' || !(letter in NOTE_INDEX)) {
      notes.push({ freq: 0, ticks });
      continue;
    }
    let index = NOTE_INDEX[letter] + semitoneAdj;
    let noteOctave = 256;
    while (index < 0) {
      index += 12;
      noteOctave /= 2;
    }
    while (index >= 12) {
      index -= 12;
      noteOctave *= 2;
    }
    const freq = (((NOTE_FREQ[index] * octave) >> 8) * noteOctave) >> 8;
    notes.push({ freq, ticks });
  }
  return notes;
}

/** Total length of a tune in seconds at ten ticks per second. */
export function tuneSeconds(notes: TuneNote[]): number {
  return notes.reduce((s, n) => s + n.ticks, 0) / 10;
}
